import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ApplicationContextPropertiesSet, Calendar, HostService, MeetingType } from '@vendasta/meetings';
import { retryer } from '@vendasta/rx-utils';
import isEqual from 'lodash/isEqual';
import { combineLatest, Observable, of, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { LOCAL_STORAGE_TOKEN } from './local-storage';
import { MeetingSchedulerServiceModule } from './meeting-scheduler-service.module';
import {
  APPLICATION_CONTEXT_TOKEN,
  ExternalCalendar,
  MeetingSchedulerCalendarService,
  MEETING_SCHEDULER_CALENDAR_SERVICE_TOKEN,
  USER_ID_TOKEN,
  FEATURE_MEETING_SESSION_EVENT_TOKEN,
} from './providers';
import {
  meetingSchedulerCreateDefaultMeetingTypesRetryConfig,
  meetingSchedulerInitializationRetryConfig,
} from './retry';

export interface UserIdWithApplicationContext {
  userId: string;
  applicationContext: ApplicationContextPropertiesSet;
}

const localStorageVersion = '1.0.1';

// This class is responsible for loading initial values required for MeetingScheduler
// It stores initialization values in LocalStorage to reduce unnecessary load on the server
@Injectable({
  providedIn: MeetingSchedulerServiceModule,
})
export class MeetingSchedulerInitializerService {
  readonly userIdWithApplicationContext$: Observable<UserIdWithApplicationContext>;
  private readonly personalCalendarId$: Observable<string>;
  private readonly sessionCalendarId$: Observable<string>;
  private readonly ensureMeetingTypesExistStep$: Observable<unknown>;

  groupCalendars$: Observable<ExternalCalendar[]>;
  personalCalendar$: Observable<Calendar>;
  sessionCalendar$: Observable<Calendar>;
  meetingTypeMap$: Observable<{ [calendarId: string]: MeetingType[] }>;

  constructor(
    @Inject(USER_ID_TOKEN) readonly userId$: Observable<string>,
    @Inject(APPLICATION_CONTEXT_TOKEN)
    private readonly applicationContext$: Observable<ApplicationContextPropertiesSet>,
    @Inject(MEETING_SCHEDULER_CALENDAR_SERVICE_TOKEN) private readonly calendarService: MeetingSchedulerCalendarService,
    @Inject(LOCAL_STORAGE_TOKEN) private readonly localStorage: Storage,
    private readonly hostService: HostService,
    @Inject(FEATURE_MEETING_SESSION_EVENT_TOKEN)
    readonly featureSessionEventEnabled$: Observable<boolean>,
  ) {
    this.userId$ = this.userId$.pipe(filter<string>(Boolean), distinctUntilChanged());
    this.applicationContext$ = this.applicationContext$.pipe(
      filter<ApplicationContextPropertiesSet>(Boolean),
      distinctUntilChanged(isEqual),
    );
    this.userIdWithApplicationContext$ = combineLatest([this.userId$, this.applicationContext$]).pipe(
      map(([userId, applicationContext]) => ({ userId, applicationContext })),
      distinctUntilChanged(isEqual),
      shareReplay(1),
    );

    this.personalCalendarId$ = this.getPersonalCalendarId$(this.userIdWithApplicationContext$);
    this.personalCalendar$ = this.personalCalendarId$.pipe(
      switchMap((calendarId) => this.hostService.getCalendar({ calendarId })),
      shareReplay(1),
    );
    this.sessionCalendarId$ = this.getSessionCalendarId$(this.userIdWithApplicationContext$);
    this.sessionCalendar$ = this.sessionCalendarId$.pipe(
      switchMap((calendarId) => {
        if (!calendarId) {
          return of(null); // Return null when no calendar ID (feature disabled)
        }
        return this.hostService.getCalendar({ calendarId }).pipe(
          catchError((error) => {
            console.error('SessionCalendar - getCalendar API failed:', error);
            return of(null);
          }),
        );
      }),
      filter<Calendar>(Boolean), // Only emit when we have a valid calendar
      shareReplay(1),
    );

    this.groupCalendars$ = this.loadGroupCalendars(this.userIdWithApplicationContext$);
    const groupCalendarIds$ = this.groupCalendars$.pipe(map((calendars) => calendars.map((calendar) => calendar.id)));
    const allCalendarIds$: Observable<{ personalCalendarId: string; groupCalendarIds: string[] }> = combineLatest([
      this.personalCalendarId$,
      groupCalendarIds$,
    ]).pipe(
      map(([personalCalendarId, groupCalendarIds]) => ({
        groupCalendarIds: (groupCalendarIds || []).sort(),
        personalCalendarId,
      })),
      distinctUntilChanged(isEqual),
    );
    this.ensureMeetingTypesExistStep$ = allCalendarIds$.pipe(
      switchMap((calendarIds) =>
        this.ensureCalendarsHaveDefaultMeetingTypes(calendarIds.personalCalendarId, calendarIds.groupCalendarIds),
      ),
      shareReplay(1),
    );
    this.meetingTypeMap$ = this.ensureMeetingTypesExistStep$.pipe(
      switchMap(() =>
        this.loadMeetingTypesForCalendars(
          allCalendarIds$.pipe(
            map((allCalendarIds) => [allCalendarIds.personalCalendarId, ...allCalendarIds.groupCalendarIds]),
          ),
        ),
      ),
      shareReplay(1),
    );
  }

  /**
   * Ensure the calendar has had their default meeting types created at some point.
   * This is to ensure there is a good experience for new users who can simply use the event types.
   * @param groupCalendarIds
   */
  private ensureCalendarsHaveDefaultMeetingTypes(
    personalCalendarId: string,
    groupCalendarIds: string[],
  ): Observable<unknown> {
    const key = buildWasDefaultMeetingTypeCreatedForCalendarsKey(groupCalendarIds);
    const wasCreated = filterNullOrUndefinedString(this.localStorage.getItem(key));
    if (key && wasCreated) {
      return of();
    }

    return this.hostService.createDefaultMeetingTypes({ calendarIds: [...groupCalendarIds, personalCalendarId] }).pipe(
      catchError((err: HttpErrorResponse) => {
        // Due to latency in sales teams sometimes the person is not properly associated with teams in time.
        // This problem usually goes away, but can cause problems for Meeting Scheduler's setup.
        // At the very least, ensure the personal calendar meeting types exist
        if (err.status === 403) {
          return this.hostService.createDefaultMeetingTypes({ calendarIds: [personalCalendarId] });
        }
        return throwError(err);
      }),
      retryer(meetingSchedulerCreateDefaultMeetingTypesRetryConfig),
      tap(() => {
        if (key) {
          this.localStorage.setItem(key, 'true');
        }
      }),
      shareReplay(1),
    );
  }

  /**
   * Ensure the group calendars for this user exists and retrieve it.
   * Uses caching to reduce the number of times the ensure call needs to happen.
   */
  private loadGroupCalendars(
    userIdWithApplicationContext$: Observable<UserIdWithApplicationContext>,
  ): Observable<ExternalCalendar[]> {
    const calendars$ = this.calendarService.getCalendars().pipe(distinctUntilChanged(isEqual));
    const groupCalendarsIdMap$ = combineLatest([userIdWithApplicationContext$, calendars$]).pipe(
      switchMap(([userIdWithApplicationContext, calendars]) => {
        const key = buildGroupCalendarsKey(
          userIdWithApplicationContext.userId,
          userIdWithApplicationContext.applicationContext,
        );
        const groupCalendarMapStr = filterNullOrUndefinedString(this.localStorage.getItem(key));
        let groupCalendarMap: GroupCalendarIdMap = {};
        if (groupCalendarMapStr) {
          groupCalendarMap = JSON.parse(groupCalendarMapStr);
        }
        const externalIds = calendars.map((c) => c.externalId);
        const missingExternalIds = externalIds.filter((externalId) => !groupCalendarMap[externalId]);
        if (missingExternalIds.length === 0) {
          return of(groupCalendarMap);
        }
        return this.hostService.ensureGroupCalendarsExist({ externalIds: missingExternalIds }).pipe(
          map((calendarIds) => {
            calendarIds.forEach((calendarId, index) => (groupCalendarMap[missingExternalIds[index]] = calendarId));
            return groupCalendarMap;
          }),
          tap(() => this.localStorage.setItem(key, JSON.stringify(groupCalendarMap))),
        );
      }),
      retryer(meetingSchedulerInitializationRetryConfig),
      shareReplay(1),
    );

    return combineLatest([calendars$, groupCalendarsIdMap$]).pipe(
      map(([calendars, groupCalendarIdMap]) => {
        if (!groupCalendarIdMap) {
          return [];
        }
        return calendars.map(
          (calendar) =>
            ({
              ...calendar,
              id: groupCalendarIdMap[calendar.externalId],
            }) as ExternalCalendar,
        );
      }),
    );
  }

  /** Load the Meeting Types as we need them for operations like rescheduling
   */
  private loadMeetingTypesForCalendars(
    calendarIds$: Observable<string[]>,
  ): Observable<{ [calendarId: string]: MeetingType[] }> {
    return calendarIds$.pipe(
      switchMap((calendarIds) => {
        if (!calendarIds || calendarIds.length === 0) {
          return of({});
        }
        return this.hostService.getMeetingTypesForCalendars({
          calendarIds: calendarIds,
          metadata: {},
        });
      }),
      retryer(meetingSchedulerInitializationRetryConfig),
      shareReplay(1),
    );
  }

  /**
   * Ensures the personal calendar exists, using cache to avoid unnecessary calls.
   * @param userIdWithApplicationContext$
   */
  private getPersonalCalendarId$(
    userIdWithApplicationContext$: Observable<UserIdWithApplicationContext>,
  ): Observable<string> {
    return userIdWithApplicationContext$.pipe(
      switchMap((userIdWithApplicationContext) => {
        const key = buildPersonalCalendarIdKey(
          userIdWithApplicationContext.userId,
          userIdWithApplicationContext.applicationContext,
        );
        const personalCalendarId = filterNullOrUndefinedString(
          this.localStorage.getItem(key + '-' + localStorageVersion),
        );
        if (personalCalendarId) {
          return of(personalCalendarId);
        }
        return this.hostService
          .ensurePersonalCalendarExists({
            userId: userIdWithApplicationContext.userId,
            applicationContextProperties: userIdWithApplicationContext.applicationContext,
          })
          .pipe(tap((calendarId) => this.localStorage.setItem(key + '-' + localStorageVersion, calendarId)));
      }),
      retryer(meetingSchedulerInitializationRetryConfig),
      distinctUntilChanged(),
      shareReplay(1),
    );
  }

  private getSessionCalendarId$(
    userIdWithApplicationContext$: Observable<UserIdWithApplicationContext>,
  ): Observable<string> {
    return this.featureSessionEventEnabled$.pipe(
      switchMap((isFeatureEnabled) => {
        if (!isFeatureEnabled) {
          return of(''); // Return empty string when feature is disabled
        }

        return userIdWithApplicationContext$.pipe(
          switchMap((userIdWithApplicationContext) => {
            const key = buildSessionCalendarIdKey(
              userIdWithApplicationContext.userId,
              userIdWithApplicationContext.applicationContext,
            );

            const sessionCalendarId = filterNullOrUndefinedString(
              this.localStorage.getItem(key + '-' + localStorageVersion),
            );

            if (sessionCalendarId) {
              return of(sessionCalendarId);
            }

            // Access properties from the applicationContext parameter
            const applicationContext = userIdWithApplicationContext.applicationContext;
            const context = (applicationContext as any).user_context;

            let externalId = '';

            if (context === 'SMB') {
              externalId = (applicationContext as any).business_id;
            } else {
              externalId = (applicationContext as any).partner_id;
            }

            return this.hostService
              .ensureSessionEventCalendarsExist({
                externalId: externalId || externalId,
              })
              .pipe(
                tap((calendarId) => {
                  this.localStorage.setItem(key + '-' + localStorageVersion, calendarId);
                }),
              );
          }),
        );
      }),
      retryer(meetingSchedulerInitializationRetryConfig),
      distinctUntilChanged(),
      shareReplay(1),
    );
  }
}

// Map of externalId -> personalCalendarId
interface GroupCalendarIdMap {
  [externalId: string]: string;
}

function buildPersonalCalendarIdKey(userId: string, applicationContext: ApplicationContextPropertiesSet): string {
  const domain = getDomain();
  const applicationContextKeyPart = getApplicationContextKeyPart(applicationContext);
  return `meeting-scheduler-${domain}-${userId}-${applicationContextKeyPart}-personal-calendar`;
}

function buildSessionCalendarIdKey(userId: string, applicationContext: ApplicationContextPropertiesSet): string {
  const domain = getDomain();
  const applicationContextKeyPart = getApplicationContextKeyPart(applicationContext);
  return `meeting-scheduler-${domain}-${userId}-${applicationContextKeyPart}-session-calendar`;
}

function buildGroupCalendarsKey(userId: string, applicationContext: ApplicationContextPropertiesSet): string {
  const domain = getDomain();
  const applicationContextKeyPart = getApplicationContextKeyPart(applicationContext);
  return `meeting-scheduler-${domain}-${userId}-${applicationContextKeyPart}-group-calendars`;
}

function buildWasDefaultMeetingTypeCreatedForCalendarsKey(calendarIds: string[]): string {
  return (calendarIds || []).sort().reduce((key, calendarId) => {
    return `${key}-${calendarId}`;
  }, '');
}

function getDomain(): string {
  let domain = '';
  if (typeof location !== 'undefined' && location.hostname) {
    domain = location.hostname;
  }
  return domain;
}

function getApplicationContextKeyPart(applicationContext: ApplicationContextPropertiesSet): string {
  return Object.keys(applicationContext)
    .sort()
    .reduce((key, property) => {
      return `${key}-${property}-${applicationContext[property]}`;
    }, '');
}

function filterNullOrUndefinedString(s: string): string {
  if (s === null || s === undefined || s === 'undefined' || s === 'null') {
    return '';
  }
  return s;
}
